import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/shop.css';
import PurchaseModal from '../components/PurchaseModal';
import Preloader from '../../../shared/components/Preloader';
import PackPurchaseModal from '../components/PackPurchaseModal';
import TabSwitch from '../../../shared/components/TabSwitch';
// Same chest popup as BattlePass / Inventory → Cases (contents come from the lootbox store)
import ChestInfoModal from '../../lootbox/components/ChestInfoModal';
import {
  fetchLootboxChestTypes, fetchLootboxContents, fetchLootboxInventory,
} from '../../lootbox/store/lootboxSlice';
import TicketConverterModal from '../../tickets/components/TicketConverterModal';
import DatumPassOfferModal from '../../datumPass/components/DatumPassOfferModal';
import { useUserRedux } from '../../../hooks/useUserRedux';
import { useShopRedux } from '../hooks/useShopRedux';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { fetchAllShopItems, fetchShopPacks } from '../store/shopSlice';
import { fetchDatumPassStatus } from '../../datumPass/store/datumPassSlice';
import { fetchWalletBalance } from '../../wallet/store/walletSlice';
import { fetchNotifications } from '../../../store/slices/countersSlice';
import { shopAPI, adRewardsAPI } from '../../../services/api';
import { showAdAndGrant as showAdsgramAdAndGrant } from '../../../shared/utils/adsgram';
import { launchRewardBurst } from '../../../shared/utils/rewardAnimation';
import { normalizeShopImg as normalizeImg } from '../utils/shopImages';
// Same artwork as the BattlePass / DatumPass top banner — keeps the offer visually consistent
import datumPassBgImg from '../../../images/datumpass/bg.png';
import datumPassCrownImg from '../../../images/datumpass/bp_crown.png';
import { useTranslation } from 'react-i18next';
import { entityText } from '../../../i18n/entityText';
import { formatDate, formatNumber } from '../../../i18n/format';

/* ── Tabs ─────────────────────────────────────────────────────── */
// Tabs are a pure client-side filter over the items already in the store:
// goods are fetched once on mount, switching a tab never refetches.
// key === shop_items.type (COINS / KEY / CHEST / LIVES); 'ALL' shows the page as before.
const SHOP_TABS = [
  { key: 'ALL',   label: 'All' },
  { key: 'COINS', label: 'Coins' },
  { key: 'KEY',   label: 'Keys' },
  { key: 'CHEST', label: 'Chests' },
  { key: 'LIVES', label: 'Lives' },
];

const shopTabLabel = (t, key) => ({
  ALL: t('shop.tab.all', 'All'),
  COINS: t('shop.tab.coins', 'Coins'),
  KEY: t('shop.tab.keys', 'Keys'),
  CHEST: t('shop.tab.chests', 'Chests'),
  LIVES: t('shop.tab.lives', 'Lives'),
}[key]);

/* ── Small presentational pieces ─────────────────────────────── */

/** Discount / Best-offer badge that overflows the card corner */
function ItemBadge({ className, text, right = false }) {
  return (
    <div className={`${className} item-badge${right ? ' right' : ''}`}>
      {text && <span className="item-badge-text">{text}</span>}
    </div>
  );
}

/** One slot inside the pack body (coins / tickets / module / chest / key).
    Glow colour follows the slot content, like in the design mockups. */
const PACK_SLOT_BGS = {
  tickets: 'starter-pack-bg-p',
  coins:   'starter-pack-bg-y',
  module:  'starter-pack-bg-g',
  mystery: 'starter-pack-bg-pu',
  chest:   'starter-pack-bg-b',
  key:     'starter-pack-bg-g',
};

function PackSlot({ icon, label, bgClass }) {
  return (
    <div className={`starter-pack-item ${bgClass}`}>
      <img className="starter-pack-item-img" src={icon} alt="" />
      <p className="starter-pack-item-label">{label}</p>
    </div>
  );
}

/** Data-driven pack card. Renders pack.name, packContent items, price and an optional "Most Popular" badge. */
function StarterPackBlock({ pack, onClick }) {
  const { t } = useTranslation();
  if (!pack) return null;

  const priceLabel = pack.usdt ? `$ ${parseFloat(pack.usdt).toFixed(2)}` : null;
  const pc = pack.packContent || {};

  // Build slot list from packContent
  const slots = [];

  if (pc.tickets) {
    slots.push({
      type: 'tickets',
      icon: normalizeImg(pc.ticketsImg) || '/ticket_5.png',
      label: `x${pc.tickets}`,
    });
  }
  if (pc.coins) {
    slots.push({
      type: 'coins',
      icon: normalizeImg(pc.coinsImg) || '/shop/coins/Coins_3.png',
      label: formatNumber(pc.coins),
    });
  }
  if (Array.isArray(pc.modules)) {
    pc.modules.forEach((m) => {
      slots.push({
        type: m.random ? 'mystery' : 'module',
        icon: normalizeImg(m.img) || m.urlSmall || m.url || '/shop/starterParts.png',
        label: `x${m.quantity ?? 1}`,
      });
    });
  }
  if (Array.isArray(pc.chests)) {
    pc.chests.forEach((c) => {
      slots.push({
        type: 'chest',
        icon: normalizeImg(c.img) || '/lootbox/icon_chest_green.png',
        label: `x${c.quantity ?? 1}`,
      });
    });
  }
  if (Array.isArray(pc.keys)) {
    pc.keys.forEach((k) => {
      slots.push({
        type: 'key',
        icon: normalizeImg(k.img) || '/lootbox/icon_key_green.png',
        label: `x${k.quantity ?? 1}`,
      });
    });
  }

  return (
    <div className="starter-pack" onClick={onClick}>
      {pack.isPopular && (
        <img
          className="starter-pack-popular"
          src="/shop/mostPopular.png"
          alt={t('shop.most_popular', 'Most Popular')}
        />
      )}

      {pack.name && (
        <span className="starter-pack-title">{entityText(t, 'shop_pack', pack.id, 'name', pack.name)}</span>
      )}

      <div className="starter-pack-body">
        {slots.map((s, i) => (
          <PackSlot
            key={i}
            icon={s.icon}
            label={s.label}
            bgClass={PACK_SLOT_BGS[s.type] || 'starter-pack-bg-y'}
          />
        ))}
      </div>

      <div className="starter-pack-price">{priceLabel || t('shop.free', 'Free')}</div>
    </div>
  );
}

/* ── Main page component ──────────────────────────────────────── */
const ShopPage = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal]         = useState(false);
  const [selectedItem, setSelectedItem]   = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedList, setSelectedList]   = useState([]);   // list the opened item belongs to (arrows cycle within it)
  const location = useLocation();
  // Deep link to a tab: navigate('/shop', { state: { tab: 'KEY' } }) — e.g. "Buy" in the Inventory key popup
  const [activeTab, setActiveTab]         = useState(
    () => (SHOP_TABS.some((tab) => tab.key === location.state?.tab) ? location.state.tab : 'ALL')
  );
  const [showPackModal, setShowPackModal] = useState(false);
  const [selectedPack, setSelectedPack]   = useState(null);
  const [showConverter, setShowConverter] = useState(false);
  const [showDatumPassModal, setShowDatumPassModal] = useState(false);
  const [freeClaimStatuses, setFreeClaimStatuses] = useState({});
  const [freeClaimLoading, setFreeClaimLoading]   = useState({});
  const [, setTick] = useState(0);   // drives countdown re-renders

  const [ticketStatus, setTicketStatus]       = useState(null);
  const [ticketAdLoading, setTicketAdLoading] = useState(false);
  // Ad watched → the ticket is unlocked in the cell and waits for the Claim tap (the server has already
  // credited it; Claim plays the flight and refreshes the balance)
  const [ticketReady, setTicketReady]         = useState(false);

  const isDatumPassEnabled = useSelector(state => state.datumPass.isEnabled);
  const datumPassExpiresAt = useSelector(state => state.datumPass.expiresAt);

  // ── Chest info popup (ⓘ on CHEST cards) ─────────────────────
  const navigate = useNavigate();
  const { chestTypes, contentsByChestType, contentsLoaded, typesLoaded, chests, keys } =
    useSelector(state => state.lootbox);
  const [infoChest, setInfoChest] = useState(null); // transformed shop item of type CHEST

  // Prevents mount-effects from re-firing while an ad is playing
  const adInProgressRef = useRef(false);

  const { data: user, refreshUser } = useUserRedux();
  const { items: shopItems, packs: shopPacks, isLoading: shopLoading } = useShopRedux();
  const dispatch = useDispatch();

  // ── Load data once on mount ──────────────────────────────────
  useEffect(() => {
    if (adInProgressRef.current) return;
    dispatch(fetchAllShopItems());
    dispatch(fetchShopPacks());
    dispatch(fetchDatumPassStatus());
    adRewardsAPI.getFreeTicketStatus().then(setTicketStatus).catch(() => {});
  }, [dispatch]);

  // Ad cooldown is over → re-read the status so the cell turns back into "Watch Ad"
  useEffect(() => {
    if (!ticketStatus || ticketStatus.isAvailable || !ticketStatus.nextAvailableTime) return;
    const ms = new Date(ticketStatus.nextAvailableTime).getTime() - Date.now();
    const timer = setTimeout(() => {
      adRewardsAPI.getFreeTicketStatus().then(setTicketStatus).catch(() => {});
    }, Math.max(ms, 0) + 1000);
    return () => clearTimeout(timer);
  }, [ticketStatus]);

  // ── Transform raw API item → display object ──────────────────
  const transformItem = (item) => {
    const usdt        = item.usdt         ? parseFloat(item.usdt)         : null;
    const origUsdt    = item.originalPrice ? parseFloat(item.originalPrice) : null;
    const hasDiscount = origUsdt !== null && usdt !== null && origUsdt > usdt;

    const discountPercent = item.discountPercent != null
      ? item.discountPercent > 0 ? `${item.discountPercent}%` : null
      : hasDiscount ? `${Math.round((1 - usdt / origUsdt) * 100)}%` : null;

    return {
      id:            item.id,
      label:
        item.type === 'COINS'
          ? formatNumber(item.coinsAmount ?? item.value) || '0'
          : item.type === 'TICKETS'
          ? String(item.ticketsAmount ?? item.value ?? '0')
          : item.type === 'LIVES'
          ? t('shop.lives_n', { count: Number(item.value) || 0, defaultValue_one: '{{count}} life', defaultValue_other: '{{count}} lives' })
          : entityText(t, 'shop_item', item.id, 'name', item.name),
      img:    normalizeImg(item.imageUrl)
        || (item.type === 'LIVES' ? '/lair/heart.svg' : null)
        || ((item.maxDailyClaims ?? 0) > 0
          ? '/shop/coins/icon_coin.png'
          : '/shop/coins/Coins_0.png'),
      shine:  item.type === 'KEY'
        ? '/shop/shine/shine_green.png'
        : item.type === 'CHEST'
        ? '/shop/shine/shine_blue.png'
        : item.type === 'LIVES'
        ? '/shop/shine/shine_pink.png'
        : '/shop/shine/shine_yellow.png',
      price:         usdt !== null    ? `$${usdt.toFixed(2)}`     : null,
      originalPrice: hasDiscount      ? `${origUsdt.toFixed(2)}`  : null,
      discountPercent,
      isBestOffer:   item.isBestOffer ?? false,
      free:          (item.maxDailyClaims ?? 0) > 0,
      maxDailyClaims: item.maxDailyClaims ?? 0,
      priceStars:    item.priceStars ?? null,
      priceTON:      item.priceTON   ?? null,
      shopItem:      item,
    };
  };

  const tonItems   = (shopItems.COINS ?? []).map(transformItem);
  const keyItems   = (shopItems.KEY   ?? []).map(transformItem);
  // CHEST items: image comes from chest_types.image_url (ShopServiceImpl.toDTO), label = item name
  const chestItems = (shopItems.CHEST ?? []).map(transformItem);
  // v1.51.0: lives of mini-games (value = lives per unit)
  const livesItems = (shopItems.LIVES ?? []).map(transformItem);

  // ── Free-claim statuses ──────────────────────────────────────
  useEffect(() => {
    if (adInProgressRef.current) return;
    const freeItems = (shopItems.COINS ?? []).filter(i => (i.maxDailyClaims ?? 0) > 0);
    freeItems.forEach(async (item) => {
      try {
        const status = await shopAPI.getFreeClaimStatus(item.id);
        setFreeClaimStatuses(prev => ({ ...prev, [item.id]: status }));
      } catch (_) {}
    });
  }, [shopItems]);

  // ── Countdown ticker ─────────────────────────────────────────
  useEffect(() => {
    const coinsExhausted = Object.values(freeClaimStatuses).some(
      s => s.remainingClaims <= 0 && s.nextClaimAt
    );
    const ticketExhausted = ticketStatus && !ticketStatus.isAvailable && ticketStatus.nextAvailableTime;
    if (!coinsExhausted && !ticketExhausted) return;
    const timer = setInterval(() => setTick(n => n + 1), 1000);
    return () => clearInterval(timer);
  }, [freeClaimStatuses, ticketStatus]);

  const formatCountdown = (nextClaimAt) => {
    if (!nextClaimAt) return null;
    const diff = new Date(nextClaimAt).getTime() - Date.now();
    if (diff <= 0) return null;
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    const s = Math.floor((diff % 60_000) / 1_000);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // ── Helpers ───────────────────────────────────────────────────

  /** Show AdsGram ad then call backend webhook-style via poll. Returns true if reward granted.
      Shared flow lives in shared/utils/adsgram.js (block id comes from admin settings). */
  const showAdAndGrant = useCallback((rewardType, shopItemId = null) =>
    showAdsgramAdAndGrant(rewardType, {
      shopItemId,
      onAdStart: () => { adInProgressRef.current = true; },
      onAdEnd:   () => { adInProgressRef.current = false; },
    }), []);

  // ── Handlers ─────────────────────────────────────────────────
  const openModal = (item, index = 0, list = tonItems) => {
    setSelectedItem(item);
    setSelectedIndex(index);
    setSelectedList(list);
    setShowModal(true);
  };

  const handlePackClick = (pack) => {
    setSelectedPack({ ...pack, type: 'PACK' });
    setShowPackModal(true);
  };

  // Lootbox catalog + contents are loaded lazily on the first ⓘ tap (static per session);
  // the inventory is refreshed on every tap so owned counters in the popup are current.
  const openChestInfo = useCallback((item) => {
    if (!typesLoaded) dispatch(fetchLootboxChestTypes());
    if (!contentsLoaded) dispatch(fetchLootboxContents());
    dispatch(fetchLootboxInventory());
    setInfoChest(item);
  }, [dispatch, typesLoaded, contentsLoaded]);

  const chestQtyByType = useMemo(() => {
    const m = {};
    for (const c of chests) m[c.chestTypeId] = c.quantity;
    return m;
  }, [chests]);

  const keyQtyByType = useMemo(() => {
    const m = {};
    for (const k of keys) m[k.keyTypeId] = k.quantity;
    return m;
  }, [keys]);

  // Catalog entry for the tapped chest; falls back to the shop item's own name/image
  // so the popup opens immediately while the catalog is still loading.
  const infoChestType = useMemo(() => {
    if (!infoChest) return null;
    const id = infoChest.shopItem?.chestTypeId;
    return chestTypes.find(c => c.id === id)
      || { id, name: infoChest.label || t('reward.chest', 'Chest'), imageUrl: infoChest.img, rarity: null };
  }, [infoChest, chestTypes, t]);

  // Free coins ("25 coins"): granted directly, no ad. The backend enforces the limit
  // (shop_items.max_daily_claims — now 1 per rolling 24h) and returns 4xx when exhausted.
  const handleFreeItemClick = useCallback(async (item) => {
    const status = freeClaimStatuses[item.id];
    if (status && status.remainingClaims <= 0) return;
    setFreeClaimLoading(prev => ({ ...prev, [item.id]: true }));
    try {
      await shopAPI.claimFreeItem(item.id);
      refreshUser();
      dispatch(fetchWalletBalance());
      dispatch(fetchNotifications());
    } catch (e) {
      console.error('Free claim failed:', e);
    } finally {
      // Always re-read status: on success it shows the countdown, on "limit reached" it self-corrects
      try {
        const next = await shopAPI.getFreeClaimStatus(item.id);
        setFreeClaimStatuses(prev => ({ ...prev, [item.id]: next }));
      } catch (_) {}
      setFreeClaimLoading(prev => ({ ...prev, [item.id]: false }));
    }
  }, [freeClaimStatuses, refreshUser, dispatch]);

  // Watch Ad → (ticket unlocked) → Claim → ticket flies into the balance
  const handleFreeTicketClick = useCallback(async (e) => {
    if (ticketAdLoading) return;
    if (ticketReady) {
      const img = e?.currentTarget?.querySelector('.item-img');
      launchRewardBurst({
        startRect: img?.getBoundingClientRect(),
        flights: [{ iconSrc: '/ticket_icon.png', target: 'tickets' }],
        count: 6,
      });
      setTicketReady(false);
      // balance changes when the tickets land
      setTimeout(() => {
        refreshUser();
        dispatch(fetchWalletBalance());
      }, 1000);
      return;
    }
    if (!ticketStatus || !ticketStatus.isAvailable) return;
    setTicketAdLoading(true);
    try {
      const granted = await showAdAndGrant('FREE_TICKET');
      if (granted) {
        setTicketReady(true);
        // WATCH_ADS task counter is bumped right after the grant commits
        setTimeout(() => dispatch(fetchNotifications()), 1500);
        const next = await adRewardsAPI.getFreeTicketStatus();
        setTicketStatus(next);
      }
    } catch (e) {
      console.error('Free ticket claim failed:', e);
    } finally {
      setTicketAdLoading(false);
    }
  }, [ticketAdLoading, ticketReady, ticketStatus, refreshUser, showAdAndGrant, dispatch]);

  // Leaving the shop with an unclaimed ticket: it is already credited — just sync the balance
  const ticketReadyRef = useRef(false);
  ticketReadyRef.current = ticketReady;
  useEffect(() => () => {
    if (ticketReadyRef.current) dispatch(fetchWalletBalance());
  }, [dispatch]);

  // ── Render one item cell ──────────────────────────────────────
  // `list` — the category list the cell belongs to, so PurchaseModal arrows cycle inside it
  const renderItem = (item, i, list = tonItems) => {
    const claimStatus   = item.free ? freeClaimStatuses[item.id] : null;
    const isExhausted   = claimStatus && claimStatus.remainingClaims <= 0;
    const countdown     = isExhausted ? formatCountdown(claimStatus.nextClaimAt) : null;
    const isLoadingClaim = !!freeClaimLoading[item.id];

    const dailyLabel = claimStatus
      ? `${claimStatus.remainingClaims}/${claimStatus.maxClaims}`
      : `${item.maxDailyClaims}/${item.maxDailyClaims}`;

    const handleClick = () => {
      if (item.free) {
        if (!isExhausted && !isLoadingClaim) handleFreeItemClick(item);
      } else {
        openModal(item, i, list);
      }
    };

    const isChest = item.shopItem?.type === 'CHEST';

    return (
      <div key={item.id ?? `item-${i}`} className="item-wrap">
        {item.discountPercent && (
          <ItemBadge className="item-discount" text={item.discountPercent} />
        )}
        {item.isBestOffer && !isChest && (
          <ItemBadge className="item-best-offer" right />
        )}
        {isChest && (
          <button
            type="button"
            className="item-info-btn"
            aria-label={t('shop.chest_info_aria', 'What is inside this chest')}
            onClick={(e) => { e.stopPropagation(); openChestInfo(item); }}
          >
            <img src="/shop/info.png" alt="" />
          </button>
        )}

        <div
          className={[
            'shop-item',
            item.free      ? 'free'      : '',
            isExhausted    ? 'exhausted' : '',
          ].filter(Boolean).join(' ')}
          onClick={handleClick}
        >
          <div className="item-body">
            {item.free && (
              <span className="item-daily">
                {isLoadingClaim ? '...' : dailyLabel}
              </span>
            )}
            <img className="item-shine" src={item.shine} alt="" aria-hidden="true" />
            <img className="item-img" src={item.img} alt={item.label} />
            <h3 className="item-label">{item.label}</h3>
          </div>

          <div className="item-price">
            {item.originalPrice && (
              <span className="price-old">{item.originalPrice}</span>
            )}
            {item.free && !isExhausted && (
              <span className="price-free">
                {isLoadingClaim ? '...' : t('shop.free', 'Free')}
              </span>
            )}
            {item.free && isExhausted && (
              countdown
                ? <span className="price-countdown">{countdown}</span>
                : <span className="price-free">{t('shop.free', 'Free')}</span>
            )}
            {!item.free && item.price && (
              <span className="price-new">{item.price}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Free-ticket cell (rendered inside the main grid) ──────────
  // States: Watch Ad (ticket locked) → Claim (unlocked, glowing) → countdown to the next ad
  const renderFreeTicketCell = () => {
    const unavailable = !ticketReady && (!ticketStatus || !ticketStatus.isAvailable);
    const countdown   = unavailable && ticketStatus?.nextAvailableTime
      ? formatCountdown(ticketStatus.nextAvailableTime)
      : null;
    const locked = !ticketReady && !unavailable;

    return (
      <div key="free-ticket" className="item-wrap">
        <div
          className={[
            'shop-item',
            'free',
            'ad-ticket',
            ticketReady ? 'ad-ticket--ready' : '',
            locked ? 'ad-ticket--locked' : '',
            unavailable ? 'exhausted' : '',
          ].filter(Boolean).join(' ')}
          onClick={handleFreeTicketClick}
        >
          <div className="item-body">
            <img className="item-shine" src="/shop/shine/shine_pink.png" alt="" aria-hidden="true" />
            <img className="item-img" src="/ticket_icon.png" alt={t('shop.free_ticket', 'Free ticket')} />
            {locked && (
              <span className="ad-ticket__badge" aria-hidden="true">
                <img src="/adv_icon.svg" alt="" />
              </span>
            )}
            <h3 className="item-label">1</h3>
          </div>
          <div className="item-price">
            {ticketAdLoading ? (
              <span className="price-free">...</span>
            ) : ticketReady ? (
              <span className="price-free">{t('common.claim', 'Claim')}</span>
            ) : unavailable ? (
              countdown
                ? <span className="price-countdown">{countdown}</span>
                : <span className="price-free">{ticketStatus ? t('shop.not_available', 'Not available') : '...'}</span>
            ) : (
              <span className="price-free ad-ticket__watch">
                <img src="/adv_icon.svg" alt="" />{t('shop.watch_ad', 'Watch Ad')}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const loading = shopLoading && !tonItems.length && !keyItems.length && !chestItems.length && !livesItems.length;

  const showAll    = activeTab === 'ALL';
  const showCoins  = showAll || activeTab === 'COINS';
  const showKeys   = showAll || activeTab === 'KEY';
  const showChests = showAll || activeTab === 'CHEST';
  const showLives  = showAll || activeTab === 'LIVES';

  // Build a single ordered list of cells (filtered by the active tab):
  //   paid coins → free coins (e.g. "25 coins") → free-ticket → keys → chests → lives
  // The free-ticket is injected right after the FIRST free coin item so it stays in sequence.
  // DatumPass card, packs and the free-ticket cell belong to no category → only on "All".
  const gridCells = [];
  let ticketInserted = false;
  if (showCoins) {
    tonItems.forEach((item, i) => {
      gridCells.push(renderItem(item, i, tonItems));
      if (item.free && !ticketInserted && showAll) {
        gridCells.push(renderFreeTicketCell());
        ticketInserted = true;
      }
    });
  }
  if (showAll && !ticketInserted) {
    gridCells.push(renderFreeTicketCell());
  }
  if (showKeys) {
    keyItems.forEach((item, i) => {
      gridCells.push(renderItem(item, i, keyItems));
    });
  }
  if (showChests) {
    chestItems.forEach((item, i) => {
      gridCells.push(renderItem(item, i, chestItems));
    });
  }
  if (showLives) {
    livesItems.forEach((item, i) => {
      gridCells.push(renderItem(item, i, livesItems));
    });
  }

  return (
    <div className="shop-page">
      <TabSwitch tabs={SHOP_TABS.map((tab) => ({ ...tab, label: shopTabLabel(t, tab.key) }))} activeTab={activeTab} onTabChange={setActiveTab} />

      <div className="shop-scroll">
        {/* ── DatumPass offer — always first (only on "All") ── */}
        {showAll && (
        <div
          className={`shop-datumpass-card${isDatumPassEnabled ? ' shop-datumpass-card--active' : ''}`}
          style={{ backgroundImage: `url(${datumPassBgImg})` }}
          onClick={() => setShowDatumPassModal(true)}
        >
          <div className="shop-datumpass-overlay" />
          <img className="shop-datumpass-crown" src={datumPassCrownImg} alt="" />
          <div className="shop-datumpass-body">
            <div className="shop-datumpass-kicker">
              {isDatumPassEnabled ? t('shop.pass.yours', 'Your subscription') : t('shop.pass.days30', '30-day subscription')}
            </div>
            <div className="shop-datumpass-title">DatumPass</div>
            <ul className="shop-datumpass-perks">
              <li>{t('shop.pass.perk_autocollect', 'Auto-collect tickets')}</li>
              <li>{t('shop.pass.perk_battlepass', 'Premium BattlePass track')}</li>
              <li>{t('shop.pass.perk_tickets', '2× free tickets · Affiliate GRAM')}</li>
            </ul>
            {isDatumPassEnabled && datumPassExpiresAt && (
              <div className="shop-datumpass-active">
                {t('shop.pass.active_until', 'Active until {{date}}', { date: formatDate(datumPassExpiresAt, { month: 'short', day: 'numeric' }) })}
              </div>
            )}
          </div>
          <button className="app-btn app-btn--yellow app-btn--sm shop-datumpass-btn">
            {isDatumPassEnabled ? t('shop.pass.renew', 'Renew') : t('shop.pass.get', 'Get Pass')}
          </button>
        </div>
        )}

        {/* ── All packs (vertical list, only on "All") ──────── */}
        {showAll && shopPacks?.length > 0 && (
          <div className="shop-packs-list">
            {shopPacks.map((pack) => (
              <StarterPackBlock
                key={pack.id}
                pack={pack}
                onClick={() => handlePackClick(pack)}
              />
            ))}
          </div>
        )}

        {/* ── Coins + Free Ticket + Keys + Chests — single grid ── */}
        {loading ? (
          <Preloader />
        ) : (
          <>
            {gridCells.length > 0 ? (
              <div className="shop-items-grid">
                {gridCells}
              </div>
            ) : (
              !showAll && <div className="shop-empty">{t('shop.empty_category', 'No items in this category yet')}</div>
            )}
          </>
        )}

      </div>

      {/* ── Convert button (fixed) ────────────────────────── */}
      <div className="shop-convert-wrap">
        <button className="app-btn app-btn--purple" onClick={() => setShowConverter(true)}>
          {t('shop.converter', 'Coins-to-tickets converter')}
        </button>
      </div>

      {/* ── Modals ────────────────────────────────────────── */}
      {showConverter && (
        <TicketConverterModal
          onClose={() => { setShowConverter(false); refreshUser(); dispatch(fetchWalletBalance()); }}
        />
      )}
      {showDatumPassModal && (
        <DatumPassOfferModal
          onClose={() => {
            setShowDatumPassModal(false);
            dispatch(fetchDatumPassStatus());
            dispatch(fetchWalletBalance());
            dispatch(fetchNotifications());
          }}
        />
      )}
      {showModal && selectedItem && (
        <PurchaseModal
          item={selectedItem}
          index={selectedIndex}
          items={selectedList.length ? selectedList : tonItems}
          onClose={() => { setShowModal(false); refreshUser(); }}
          onChange={(newItem, newIndex) => {
            setSelectedItem(newItem);
            setSelectedIndex(newIndex);
          }}
        />
      )}
      {showPackModal && selectedPack && (
        <PackPurchaseModal
          pack={selectedPack}
          onClose={() => setShowPackModal(false)}
        />
      )}
      {/* Chest contents popup — shared with BattlePass / Inventory → Cases */}
      {infoChestType && (
        <ChestInfoModal
          chestType={infoChestType}
          contents={contentsByChestType[infoChestType.id]}
          contentsLoading={!contentsLoaded}
          chestQty={chestQtyByType[infoChestType.id] || 0}
          keyQty={keyQtyByType[infoChestType.keyTypeId] || 0}
          openLabel={t('lootbox.open_in_inventory', 'Open in Inventory')}
          onOpen={() => navigate('/inventory', { state: { mainTab: 'cases' } })}
          onClose={() => setInfoChest(null)}
        />
      )}
    </div>
  );
};

export default ShopPage;
